import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { architectAPI } from '../api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Building, Calendar, CheckCircle2, ChevronRight,
  Clock, MapPin, Ruler, ShieldCheck, BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList, CartesianGrid } from 'recharts';

// Presentation-only tone map for KPI tiles (matches the approved reference palette).
const TONE = {
  green: 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-300',
  amber: 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-300',
  red: 'bg-red-50 border-red-100 text-red-600 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300',
  blue: 'bg-blue-50 border-blue-100 text-blue-700 dark:bg-blue-950/30 dark:border-blue-900 dark:text-blue-300',
  violet: 'bg-violet-50 border-violet-100 text-violet-700 dark:bg-violet-950/30 dark:border-violet-900 dark:text-violet-300',
  slate: 'bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-900/40 dark:border-slate-700 dark:text-slate-200',
};
const BAR_COLORS = {
  pending_architect_action: '#8b5cf6',
  my_open_tasks: '#3b82f6',
  revision_required: '#ef4444',
  awaiting_client_approval: '#f59e0b',
  tasks_due_today: '#10b981',
};
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning!' : h < 17 ? 'Good afternoon!' : 'Good evening!';
};
import {
  formatTimeSinceAssignment,
  getArchitectProjectDueDate,
  getArchitectProjectStatus,
  isArchitectProjectDelayed
} from '../lib/architectMetrics';
import { useAuth } from '../context/AuthContext';

const TASK_STATUSES = [
  { v: 'not_started', l: 'Not Started', c: 'bg-slate-100 text-slate-700' },
  { v: 'working', l: 'In Progress', c: 'bg-blue-100 text-blue-700' },
  { v: 'waiting_review', l: 'Waiting Review', c: 'bg-purple-100 text-purple-700' },
  { v: 'revision', l: 'Revision', c: 'bg-amber-100 text-amber-700' },
  { v: 'completed', l: 'Completed', c: 'bg-emerald-100 text-emerald-700' },
  { v: 'delivered', l: 'Delivered', c: 'bg-green-100 text-green-700' }
];

// Simplified 3-group KPI layout. Keys are the SAME live /architect/dashboard card keys — no new counts,
// no hard-coded numbers. Metrics not listed here (total_assigned_projects, deliverables_in_progress,
// deliverables_completed, pending_client_approval, ready_for_ifc_release, handover_pending, ready_to_close,
// closed_projects, manager_cards) are only HIDDEN from this view — backend counts and /dashboard/list remain intact.
const CARD_GROUPS = [
  { id: 'projects', title: 'Projects', subtitle: 'Overview of your assigned projects', icon: Building, tone: 'amber', items: [
    ['active_projects', 'Active', 'green'],
    ['due_soon', 'Deliverables Due Soon', 'amber'],
    ['overdue', 'Overdue Deliverables', 'red'],
    ['ready_for_handover', 'Handover', 'blue'],
  ] },
  { id: 'my-work', title: 'My Work', subtitle: 'Tasks and deliverables to work on', icon: CheckCircle2, tone: 'blue', items: [
    ['tasks_due_today', 'Due Today', 'green'],
    ['pending_architect_action', 'Pending Action', 'violet'],
    ['my_open_tasks', 'Open Tasks', 'blue'],
    ['revision_required', 'Revision Required', 'red'],
  ] },
  { id: 'approval', title: 'Approval', subtitle: 'Internal and client approvals', icon: ShieldCheck, tone: 'green', items: [
    ['awaiting_internal_review', 'Internal Review', 'violet'],
    ['awaiting_client_approval', 'Client Approval', 'blue'],
    ['client_changes_requested', 'Changes Requested', 'red'],
    ['client_approved', 'Approved', 'green'],
  ] },
];

// "My Work Status" graph — reuses the exact same dash.cards values already fetched. No extra API call.
const WORK_CHART_KEYS = [
  ['pending_architect_action', 'Pending Action'],
  ['my_open_tasks', 'Open Tasks'],
  ['revision_required', 'Revision'],
  ['awaiting_client_approval', 'Client Approval'],
  ['tasks_due_today', 'Due Today'],
];

export const ArchitectDashboardPage = ({ mine = false }) => {
  const { user } = useAuth();
  const userRole = user?.role;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [dash, setDash] = useState(null);
  const [drill, setDrill] = useState(null);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      // backend restricts notifications to architect roles; other roles would only get a 403
      const notificationRequest = (userRole === 'architect' || userRole === 'architect_manager')
        ? architectAPI.notifications().catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });
      const [projectsRes, noticesRes, dashRes] = await Promise.all([
        architectAPI.projects(mine), notificationRequest,
        architectAPI.dashboard(mine).catch(() => ({ data: null }))]);
      setDash(dashRes.data);
      setProjects(projectsRes.data || []);
      setNotifications(noticesRes.data || []);
    }
    catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  }, [userRole, mine]);
  useEffect(() => { fetch(); }, [fetch]);

  const openCard = async (key, label) => {
    setDrill({ card: key, label, loading: true, items: [] });
    try {
      const r = await architectAPI.dashboardList(key, undefined, mine);
      setDrill({ card: key, label, loading: false, ...r.data });
    } catch (e) { setDrill(null); toast.error(e.response?.data?.detail || 'Could not open list'); }
  };

  const updateStatus = async (leadId, taskStatus) => {
    try { await architectAPI.updateTaskStatus(leadId, taskStatus); toast.success('Task status updated'); fetch(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const projectById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);

  // Filters are derived purely from the already-loaded project list. No new API.
  const matchesFilter = (p, key) => {
    if (key === 'all') return true;
    if (key === 'overdue') return isArchitectProjectDelayed(p);
    const s = getArchitectProjectStatus(p);
    if (key === 'in_progress') return ['started', 'working', 'in_progress', 'revision', 'revision_in_progress', 'under_revision'].includes(s);
    if (key === 'waiting_review') return ['waiting_review', 'work_submitted'].includes(s);
    if (key === 'client_approval') return ['waiting_approval', 'approval_pending'].includes(s);
    return true;
  };
  const visibleProjects = projects.filter(p => matchesFilter(p, filter));


  return (
    <div className="space-y-4" data-testid="architect-container">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-outfit font-semibold">{mine ? 'My Workplace' : 'Architect Workspace'}</h1>
          <p className="text-sm text-muted-foreground">{mine
            ? 'Only your own assigned projects, tasks and approvals. Financial data is not shown.'
            : 'Your projects, tasks and approvals in one place. Financial data is not shown.'}</p>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <Calendar className="h-5 w-5 text-primary" />
          <div className="leading-tight">
            <p className="text-sm font-semibold">{new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</p>
            <p className="text-xs text-muted-foreground">{greeting()}</p>
          </div>
        </div>
      </div>

      {dash?.cards && (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3" data-testid="architect-dashboard-cards">
          {CARD_GROUPS.map(group => (
            <div key={group.title} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid={`dash-group-${group.id}`}>
              <div className="mb-3 flex items-center gap-3">
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg border ${TONE[group.tone]}`}>
                  <group.icon className="h-4.5 w-4.5" />
                </span>
                <div className="min-w-0 flex-1 leading-tight">
                  <p className="text-base font-semibold">{group.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{group.subtitle}</p>
                </div>
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                {group.items.map(([key, label, tone]) => (
                  <button key={key} data-testid={`dash-card-${key}`} onClick={() => openCard(key, label)}
                    className={`rounded-lg border px-2 py-2 text-left transition-all hover:-translate-y-0.5 ${TONE[tone]} ${
                      drill?.card === key ? 'ring-2 ring-primary/50' : ''}`}>
                    <p className="text-[10px] font-medium leading-tight opacity-80">{label}</p>
                    <p className="mt-1 text-2xl font-outfit font-semibold leading-none">
                      {(dash.cards[key] ?? dash.manager_cards?.[key] ?? 0)}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {drill && (
        <div className="clay-card p-4" data-testid="dash-drilldown">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold" data-testid="dash-drilldown-title">
              {drill.label} · <span data-testid="dash-drilldown-count">{drill.loading ? '…' : drill.count}</span>
            </p>
            <button data-testid="dash-drilldown-close" onClick={() => setDrill(null)} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
          </div>
          {drill.loading ? <p className="text-sm text-muted-foreground">Loading…</p>
            : drill.count === 0 ? <p className="text-sm text-muted-foreground" data-testid="dash-drilldown-empty">Nothing here right now.</p>
            : <ul className="divide-y text-sm">
                {drill.items.slice(0, 40).map((it, i) => (
                  <li key={it.id || i} className="flex items-center justify-between py-2" data-testid={`dash-drill-row-${i}`}>
                    <span>{it.name || it.title || it.project_name || it.customer_name}
                      {it.project_name && (it.name || it.title) ? <span className="text-muted-foreground"> · {it.project_name}</span> : null}</span>
                    <span className="flex items-center gap-2 text-xs text-muted-foreground">
                      {it.status && <span>{it.status}</span>}
                      {(it.lead_id || it.id) && drill.kind !== 'tasks' && (
                        <button className="rounded border px-2 py-0.5" data-testid={`dash-drill-open-${i}`}
                          onClick={() => navigate(`/architect/projects/${it.lead_id || it.id}`)}>Open</button>)}
                    </span>
                  </li>))}
              </ul>}
        </div>
      )}

      {dash?.cards && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid="architect-work-status-chart">
          <div className="mb-2 flex items-center gap-3">
            <span className={`flex h-9 w-9 items-center justify-center rounded-lg border ${TONE.violet}`}>
              <BarChart3 className="h-4.5 w-4.5" />
            </span>
            <div className="leading-tight">
              <p className="text-base font-semibold">My Work Status</p>
              <p className="text-xs text-muted-foreground">Current count of key tasks and approvals</p>
            </div>
          </div>
          <div className="h-[184px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical"
                data={WORK_CHART_KEYS.map(([key, label]) => ({ key, label, value: dash.cards[key] ?? 0 }))}
                margin={{ top: 4, right: 36, left: 8, bottom: 0 }} barCategoryGap="22%">
                <CartesianGrid horizontal={false} stroke="currentColor" className="text-slate-200 dark:text-slate-800" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} stroke="currentColor" className="text-muted-foreground" />
                <YAxis type="category" dataKey="label" width={132} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} stroke="currentColor" className="text-muted-foreground" />
                <Tooltip cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={18} cursor="pointer"
                  onClick={(d) => d?.payload && openCard(d.payload.key, d.payload.label)}>
                  {WORK_CHART_KEYS.map(([key]) => (
                    <Cell key={key} fill={BAR_COLORS[key]} opacity={drill && drill.card !== key ? 0.45 : 1} />
                  ))}
                  <LabelList dataKey="value" position="right" style={{ fontSize: 11, fontWeight: 600, fill: 'currentColor' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}


      {notifications.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid="architect-notifications">
          <div className="mb-2 flex items-center gap-2">
            <p className="text-lg font-semibold">Due Task Notifications</p>
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">{notifications.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs text-muted-foreground dark:bg-slate-800/60">
                <th className="rounded-l-md py-2 pl-3">Task</th><th>Project / Client</th><th>Stage / Service</th><th>Due Date</th><th>Priority</th><th className="rounded-r-md pr-3 text-center">Action</th>
              </tr></thead>
              <tbody>
                {notifications.map((n, i) => {
                  const p = projectById[n.lead_id];
                  const due = n.due_date ? new Date(n.due_date) : null;
                  const overdue = due && !Number.isNaN(due.getTime()) && due < new Date();
                  const priority = String(p?.priority || '').toLowerCase();
                  const chip = priority === 'urgent' ? TONE.red : priority === 'high' ? TONE.amber : priority === 'medium' ? TONE.amber : TONE.blue;
                  const bar = priority === 'urgent' ? 'bg-red-500' : priority === 'high' ? 'bg-orange-500' : priority === 'medium' ? 'bg-amber-400' : 'bg-blue-400';
                  return (
                    <tr key={n.id} className="border-b border-slate-100 last:border-0 dark:border-slate-800" data-testid={`architect-notification-${i}`}>
                      <td className="py-2.5 pl-3">
                        <span className="flex items-start gap-2">
                          <span className={`mt-0.5 h-5 w-1 flex-shrink-0 rounded-full ${bar}`} />
                          <span className="text-[13px]">{String(n.message || '').replace(/^Task due:\s*/i, '')}</span>
                        </span>
                      </td>
                      <td className="text-[13px]">
                        <p className="font-medium">{p ? (p.customer_name || p.project_name) : '—'}</p>
                        <p className="text-xs text-muted-foreground">{p?.project_name || p?.company_name || ''}</p>
                      </td>
                      <td className="text-[13px] text-muted-foreground">{p?.services_required?.[0] || '—'}</td>
                      <td className={`text-[13px] font-medium ${overdue ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {due && !Number.isNaN(due.getTime()) ? due.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td>
                        <span className={`inline-flex rounded-md border px-2.5 py-1 text-[11px] font-semibold ${chip}`}>
                          {priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'Not set'}
                        </span>
                      </td>
                      <td className="pr-3 text-center">
                        <button className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
                          data-testid={`architect-notification-open-${i}`}
                          onClick={() => navigate(`/architect/projects/${n.lead_id}`)}>Open</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <p className="text-lg font-semibold">Assigned Projects</p>
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-white">{projects.length}</span>
          </div>
          {projects.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="architect-project-filters">
              {[['all', 'All'], ['in_progress', 'In Progress'], ['overdue', 'Delayed Projects'],
                ['waiting_review', 'Waiting Review'], ['client_approval', 'In Client Approval']].map(([key, label]) => (
                <button key={key} data-testid={`architect-filter-${key}`} onClick={() => setFilter(key)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === key
                    ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                    : 'border-slate-200 text-muted-foreground hover:border-primary/50 dark:border-slate-700'}`}>
                  {label} ({projects.filter(p => matchesFilter(p, key)).length})
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div> :
         projects.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No projects assigned yet.</div> :
         visibleProjects.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground" data-testid="architect-projects-filter-empty">No projects in this filter.</div> :
         <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
           {visibleProjects.map(p => {
             const dueDate = getArchitectProjectDueDate(p);
             const delayed = isArchitectProjectDelayed(p);
             const priority = String(p.priority || '').toLowerCase();
             const assignmentAge = formatTimeSinceAssignment(p);
             const stage = TASK_STATUSES.find(s => s.v === p.architect_task_status);
             return (
             <div key={p.id} data-testid={`architect-project-${p.id}`}
               className={`rounded-xl border bg-white p-3 transition-all hover:shadow-md dark:bg-slate-900 ${
                 delayed || priority === 'urgent' ? 'border-red-200 dark:border-red-900/60' : 'border-slate-200 dark:border-slate-700'}`}>
               <div className="flex items-start gap-2.5">
                 <span className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border ${TONE.amber}`}>
                   <Building className="h-5 w-5" />
                 </span>
                 <div className="min-w-0 flex-1">
                   <div className="flex items-start justify-between gap-1">
                     <p className="truncate text-sm font-semibold">{p.customer_name}</p>
                     <span className={`flex-shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${
                       delayed ? TONE.red : stage?.v === 'waiting_review' ? TONE.violet : stage?.v === 'revision' ? TONE.amber : TONE.blue}`}>
                       {delayed ? 'Delayed' : (stage?.l || 'Not started')}
                     </span>
                   </div>
                   <p className="truncate text-xs text-muted-foreground">{p.project_name || p.company_name || 'Individual'}</p>
                   {p.plot_size && <p className="truncate text-[11px] text-muted-foreground">{p.plot_size}{p.construction_area ? ` · ${p.construction_area}` : ''}</p>}
                 </div>
               </div>

               <div className="mt-2.5 space-y-1.5">
                 {p.project_address && (
                   <div className="flex items-start gap-1.5 text-muted-foreground">
                     <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                     <span className="truncate text-[11px]">{p.project_address}, {p.city}</span>
                   </div>
                 )}
                 {p.plot_size && (
                   <div className="flex items-center gap-1.5 text-muted-foreground">
                     <Ruler className="h-3.5 w-3.5" />
                     <span className="text-[11px]">Plot: {p.plot_size} · Area: {p.construction_area || '—'}</span>
                   </div>
                 )}
                 <div className={`flex items-center gap-1.5 ${delayed ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                   <Calendar className="h-3.5 w-3.5" />
                   <span className="text-[11px] font-medium">{dueDate ? `Due: ${dueDate.toLocaleDateString()}` : 'Due date not set'}{delayed ? ' · Delayed' : ''}</span>
                 </div>
                 <div className="flex items-center gap-1.5 text-muted-foreground">
                   <Clock className="h-3.5 w-3.5" />
                   <span className="text-[11px]">{assignmentAge === 'Not recorded' ? 'Assignment date not recorded' : `Assigned ${assignmentAge} ago`}</span>
                 </div>
               </div>

               <div className="mt-2 flex flex-wrap gap-1">
                 {p.services_required?.slice(0, 3).map((s, i) => (
                   <span key={i} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] dark:bg-slate-800">{s}</span>
                 ))}
               </div>

               <div className="mt-2.5 border-t border-slate-200 pt-2 dark:border-slate-700">
                 <p className="mb-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">Task Status</p>
                 <div className="flex flex-wrap gap-1">
                   {TASK_STATUSES.map(s => (
                     <button key={s.v} onClick={() => updateStatus(p.id, s.v)}
                       data-testid={`status-${s.v}-${p.id}`}
                       className={`rounded px-1.5 py-0.5 text-[10px] transition-colors ${p.architect_task_status === s.v ? s.c + ' ring-1 ring-primary/50' : 'bg-slate-50 text-muted-foreground hover:bg-slate-100 dark:bg-slate-800/50'}`}>
                       {s.l}
                     </button>
                   ))}
                 </div>
                 <button onClick={() => navigate(`/architect/projects/${p.id}`)}
                   className="mt-2.5 w-full rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                   Open Project Details
                 </button>
               </div>
             </div>
             );
           })}
         </div>}
      </div>
    </div>
  );
};
export default ArchitectDashboardPage;
